import React from 'react';
import type { AccessibilityProps } from 'react-native';

import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { HybridOnPressHandler } from '@rover/kibble/core/utils';
import type {
  FontWeightProps,
  TextAlignProps,
  TextTransformProps,
} from '@rover/kibble/styles/styled-system';
import type {
  ThemeHeadingSizes,
  ThemeSpacing,
  ThemeTextDecoration,
  ThemeTextSizes,
} from '@rover/kibble/styles/themeTypes';

import type { SpacingSystemProps, SxStyleProp } from '../styledSystemPropTypes';
/** Link visual style variants */
export type LinkVariant =
  | 'primary'
  | 'secondary'
  | 'primaryContrast'
  | 'secondaryContrast'
  | 'navigation';

/** For now, this is only props that are available on web.  Ignored on React Native. */
export type LinkWebOnlyProps = {
  /** ARIA label for accessibility when link has no visible text https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-label */
  'aria-label'?: string;
  /** ARIA labelledby reference https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-labelledby */
  'aria-labelledby'?: string;
  /** HTML element type to render as */
  as?: 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
};

/** For now, this is only props that are available on Rxn.  Ignored on Web. */
export type LinkRxnOnlyProps = Omit<AccessibilityProps, 'role'>;

/** Base props without the platform-specific nested props */
export type BaseLinkProps = SpacingSystemProps &
  FontWeightProps &
  TextAlignProps &
  TextTransformProps & {
    /** Link content (text or elements) */
    children: NonNullable<React.ReactNode>;
    /** Icon component to display alongside link text */
    icon?: SvgIcon;
    /** Position of icon relative to text */
    iconAlignment?: 'left' | 'right';
    /** Theme Link sizes, (50, 100, 200, 300, 400, 500, 600) */
    size?: ThemeTextSizes | ThemeHeadingSizes;
    /** Show underline decoration */
    underline?: boolean;
    /** Visual style variant */
    variant?: LinkVariant;
    /** ID for finding this element in unit tests */
    testID?: string;
    /** Prefer using TextWithSingleInlineLink on RxN. Render link inline with text, icons not compatible on RxN */
    asInlineLink?: true;
    /** Hybrid onClick handler with both RxN and web event args - use this if you need access to the event object */
    onPress?: HybridOnPressHandler;
    /** Theme-aware custom style overrides */
    sx?:
      | SxStyleProp
      | Record<string, string | number | undefined | Array<string | number | undefined>>;
    /** URL to navigate to */
    href?: string;
    /** ONLY AFFECTS WEB. Link relationship attribute for security/SEO */
    rel?: 'noopener' | 'noreferrer' | 'nofollow' | 'noopener noreferrer';
    /** ONLY AFFECTS WEB. Link target attribute */
    target?: '_blank' | '_self' | '_parent' | '_top' | string;
  };

export type LinkCommonProps = BaseLinkProps & {
  /** Web-only props and web-specific overrides, ignored on react native */
  webProps?: Partial<Omit<BaseLinkProps, keyof LinkWebOnlyProps | 'variant'> & LinkWebOnlyProps>;
  /** RxN-only props and rxn-specific overrides, ignored on web */
  rxnProps?: Partial<Omit<BaseLinkProps, keyof LinkRxnOnlyProps | 'variant'> & LinkRxnOnlyProps>;
};

export type LinkVariantMap<T> = {
  [P in LinkVariant]: T;
};

export const LinkColorMap: LinkVariantMap<string> = {
  primary: 'interactive_link.primary_base',
  primaryContrast: 'interactive_link.primary_inverse_base',
  secondary: 'interactive_link.secondary_base',
  secondaryContrast: 'interactive_link.secondary_inverse_base',
  navigation: 'text.navigation', // used in NavBar
};

export const LinkHoverColorMap: LinkVariantMap<string> = {
  primary: 'interactive_link.primary_pressed',
  primaryContrast: 'interactive_link.primary_inverse_pressed',
  secondary: 'interactive_link.secondary_pressed',
  secondaryContrast: 'interactive_link.secondary_inverse_pressed',
  navigation: 'text.navigation_hover', // used in NavBar
};

export type LinkSizeMap<T> = {
  '50': T;
  '100': T;
  '200': T;
  '300': T;
  '400': T;
  '500': T;
  '600': T;
};

// maps icon size based on text size
export const IconSizeMap: LinkSizeMap<string> = {
  '50': '16px',
  '100': '16px',
  '200': '16px',
  '300': '16px',
  '400': '20px',
  '500': '24px',
  '600': '24px',
};

// used on RxN to recreate Heading bottom margins
export const BottomMarginMap: LinkSizeMap<ThemeSpacing> = {
  '50': '0x',
  '100': '0x',
  '200': '0x',
  '300': '0x',
  '400': '5x',
  '500': '6x',
  '600': '8x',
};

export type TypographyComponentProps<StylePropName extends 'sx' | 'style'> = {
  textDecoration: ThemeTextDecoration; // controls underline

  mb?: '0x'; // used on RxN to suppress Heading margin bottoms that mess with flexbox aligning the icon
} & {
  [P in StylePropName]: {
    color: string; // text color

    textDecorationColor?: string; // used on RxN to control underline color

    display?: P extends 'sx' ? 'inline' | 'inline-flex' : undefined; // on web we use inline for props.useInlineHeadingHack, on native we don't use this
    alignItems?: 'center'; // used on web to align icon with link text
    '&:hover'?: { color: string; textDecoration: string }; // used on web for hover styles
  };
};

export const getIsTextSized = (size: ThemeTextSizes | ThemeHeadingSizes): size is ThemeTextSizes =>
  size === '50' || size === '100' || size === '200' || size === '300';
